/**
 * Generic "new data not trained" trigger.
 * Scans for uncategorized bank tx, unassigned payroll, and unmatched POS/payout,
 * and creates the appropriate data_notifications with deduplication.
 */

import { supabase } from '@/integrations/supabase/client';
import { ensurePayrollClarificationNotification } from './payrollNotificationCreation';
import {
  createUnrecognizedExpenseNotificationIfNeeded,
  isUnrecognizedExpense,
} from './notificationCreation';
import { getUnmatchedReconciliationItems } from './salesReconciliationLogic';
import { createPOSMismatchNotificationIfNeeded } from './notificationCreation';

/**
 * Ensures one open unrecognized_expense notification per uncategorized/needs_review transaction.
 * Also auto-resolves notifications whose underlying transaction is no longer unrecognized.
 */
export async function ensureUnrecognizedExpenseNotifications(companyId: string): Promise<void> {
  const { data: rows, error } = await supabase
    .from('bank_transactions')
    .select('id, transaction_date, amount, merchant_name, description, account_id, category_type, needs_review, classification_confidence')
    .eq('company_id', companyId)
    .or('category_type.is.null,needs_review.eq.true');

  if (error) {
    console.error('ensureUnrecognizedExpenseNotifications:', error);
    return;
  }

  const qualifyingTxIds = new Set<string>();
  for (const tx of rows || []) {
    if (!isUnrecognizedExpense(tx)) continue;
    qualifyingTxIds.add(tx.id);
    const snippet = {
      merchant: tx.merchant_name ?? tx.description ?? null,
      amount: tx.amount,
      date: tx.transaction_date,
      source: null as string | null,
    };
    await createUnrecognizedExpenseNotificationIfNeeded(companyId, tx.id, snippet).catch((err) =>
      console.error('Unrecognized notification create failed:', err)
    );
  }

  try {
    const { data: openNotifs } = await (supabase as any)
      .from('data_notifications')
      .select('id, related_entity_id')
      .eq('company_id', companyId)
      .eq('type', 'unrecognized_expense')
      .eq('status', 'open')
      .limit(5000);

    for (const notif of openNotifs ?? []) {
      if (notif.related_entity_id && !qualifyingTxIds.has(notif.related_entity_id)) {
        await (supabase as any)
          .from('data_notifications')
          .update({
            status: 'resolved',
            resolved_at: new Date().toISOString(),
            resolution_payload: { auto_resolved: true, reason: 'transaction_categorized' },
            updated_at: new Date().toISOString(),
          })
          .eq('id', notif.id)
          .catch((err: unknown) => console.error('Auto-resolve unrecognized_expense failed:', err));
      }
    }
  } catch (err) {
    console.error('ensureUnrecognizedExpenseNotifications auto-resolve:', err);
  }
}

/**
 * Ensures one open payroll_clarification notification per payroll unassigned transaction.
 * Also auto-resolves notifications whose underlying transaction is no longer unassigned.
 */
export async function ensurePayrollClarificationNotifications(companyId: string): Promise<void> {
  const { data: rows, error } = await (supabase as any)
    .from('bank_transactions')
    .select('id, transaction_date, amount, merchant_name, description')
    .eq('company_id', companyId)
    .eq('subcategory', 'payroll')
    .eq('payroll_assignment_status', 'unassigned');

  if (error) {
    console.error('ensurePayrollClarificationNotifications:', error);
    return;
  }

  const unassignedTxIds = new Set((rows || []).map((tx) => tx.id));

  for (const tx of rows || []) {
    await ensurePayrollClarificationNotification(companyId, tx.id, {
      merchant: tx.merchant_name ?? tx.description ?? null,
      amount: tx.amount,
      date: tx.transaction_date,
      source: null,
    }).catch((err) => console.error('Payroll notification create failed:', err));
  }

  try {
    const { data: openNotifs } = await (supabase as any)
      .from('data_notifications')
      .select('id, related_entity_id')
      .eq('company_id', companyId)
      .eq('type', 'payroll_clarification')
      .eq('status', 'open')
      .limit(5000);

    for (const notif of openNotifs ?? []) {
      if (notif.related_entity_id && !unassignedTxIds.has(notif.related_entity_id)) {
        await (supabase as any)
          .from('data_notifications')
          .update({
            status: 'resolved',
            resolved_at: new Date().toISOString(),
            resolution_payload: { auto_resolved: true, reason: 'payroll_assigned' },
            updated_at: new Date().toISOString(),
          })
          .eq('id', notif.id)
          .catch((err: unknown) => console.error('Auto-resolve payroll_clarification failed:', err));
      }
    }
  } catch (err) {
    console.error('ensurePayrollClarificationNotifications auto-resolve:', err);
  }
}

/**
 * Cap per notification type to prevent flooding when underlying data is incomplete
 * (e.g. no bank_statements means every sales_transaction is "unmatched").
 */
const MAX_NOTIFICATIONS_PER_TYPE = 200;

/**
 * Ensures one open pos_mismatch notification per unmatched sales or bank-only item.
 * Also auto-resolves any existing open pos_mismatch notifications whose underlying
 * entity is no longer unmatched (i.e. a match was found since the notification was created).
 * Capped at MAX_NOTIFICATIONS_PER_TYPE to prevent floods from incomplete data.
 */
export async function ensurePOSMismatchNotifications(companyId: string): Promise<void> {
  let items: Awaited<ReturnType<typeof getUnmatchedReconciliationItems>>;
  try {
    items = await getUnmatchedReconciliationItems(companyId);
  } catch (err) {
    console.error('ensurePOSMismatchNotifications getUnmatched:', err);
    return;
  }

  const unmatchedEntityIds = new Set(items.map((i) => i.related_entity_id));

  // Auto-resolve open pos_mismatch notifications whose entity is no longer unmatched
  try {
    const { data: openNotifs } = await (supabase as any)
      .from('data_notifications')
      .select('id, related_entity_id')
      .eq('company_id', companyId)
      .eq('type', 'pos_mismatch')
      .eq('status', 'open')
      .limit(5000);

    for (const notif of openNotifs ?? []) {
      if (!unmatchedEntityIds.has(notif.related_entity_id)) {
        await (supabase as any)
          .from('data_notifications')
          .update({
            status: 'resolved',
            resolved_at: new Date().toISOString(),
            resolution_payload: { auto_resolved: true, reason: 'match_found' },
            updated_at: new Date().toISOString(),
          })
          .eq('id', notif.id)
          .catch((err: unknown) => console.error('Auto-resolve pos_mismatch failed:', err));
      }
    }
  } catch (err) {
    console.error('ensurePOSMismatchNotifications auto-resolve:', err);
  }

  const capped = items.slice(0, MAX_NOTIFICATIONS_PER_TYPE);
  for (const item of capped) {
    await createPOSMismatchNotificationIfNeeded(
      companyId,
      item.related_entity_type,
      item.related_entity_id,
      item.data_snippet
    ).catch((err) => console.error('POS mismatch notification create failed:', err));
  }
}

/**
 * Single entry point: ensures all data-gap notifications (unrecognized expense,
 * payroll clarification, POS mismatch) with deduplication.
 * Uses allSettled so one failure doesn't block the others.
 */
export async function refreshDataGapNotifications(companyId: string): Promise<void> {
  const results = await Promise.allSettled([
    ensureUnrecognizedExpenseNotifications(companyId),
    ensurePayrollClarificationNotifications(companyId),
    ensurePOSMismatchNotifications(companyId),
  ]);

  const labels = ['unrecognized_expense', 'payroll_clarification', 'pos_mismatch'];
  for (let i = 0; i < results.length; i++) {
    if (results[i].status === 'rejected') {
      console.error(`refreshDataGapNotifications [${labels[i]}] failed:`, (results[i] as PromiseRejectedResult).reason);
    }
  }
}
